import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { AppLink } from "../lib/route-primitives";
import { adminUsersApi, adminPlansApi } from "../lib/api";
import { ArrowLeft, Mail, Phone, Calendar, Heart, ExternalLink, Loader2, Check, X, ScrollText, CheckCircle2, XCircle } from "lucide-react";
import { cn } from "../lib/utils";
import { getPlanColors } from "../lib/plan-colors";
import { paths } from "../lib/paths";
import { isPerWeddingPlan, type AccountPackagesSummary, type WeddingPackageInfo } from "@wedding/shared";
import { PlanConversionCard } from "../components/plan-conversion-card";
import { UserPackagesCard, weddingPackageText } from "../components/user-packages-card";

interface OrganizerDetail {
  id: string;
  email: string;
  display_name: string | null;
  phone_e164: string | null;
  plan: string;
  created_at: string;
  updated_at: string;
}

interface WeddingRow {
  id: string;
  slug: string;
  title: string;
  date_time: string | null;
  location: string | null;
  status: string;
  created_at: string;
  /** Düğünün paketi (sipariş / Free / geçiş / askıda / eski kural). */
  package?: WeddingPackageInfo | null;
}

interface Plan {
  slug: string;
  label: string;
}

function PlanBadge({ plan }: { plan: string }) {
  return (
    <span className={cn(
      "inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-bold uppercase tracking-wider border",
      getPlanColors(plan).badge
    )}>
      {plan}
    </span>
  );
}

function Toast({ msg, ok, onDone }: { msg: string; ok: boolean; onDone: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onDone, 4000);
    return () => clearTimeout(timer);
  }, [onDone]);
  return (
    <div role="status" aria-live="polite" className={cn(
      "fixed bottom-6 left-1/2 -translate-x-1/2 z-[200] max-w-[calc(100vw-2rem)]",
      "flex items-center gap-2.5 px-4 py-3 rounded-2xl shadow-xl text-sm font-medium",
      ok ? "bg-emerald-500 text-white" : "bg-red-500 text-white",
    )}>
      {ok ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <XCircle className="h-4 w-4 shrink-0" />}
      <span>{msg}</span>
    </div>
  );
}

// ── Plan Change Modal ──────────────────────────────────────────

function PlanModal({
  current,
  plans,
  onClose,
  onSave,
}: {
  current: string;
  plans: Plan[];
  onClose: () => void;
  onSave: (plan: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(current);
  const [saving, setSaving]     = useState(false);
  const backdropRef = useRef<HTMLDivElement>(null);

  const changed = selected !== current;

  async function handleSave() {
    if (!changed || saving) return;
    setSaving(true);
    await onSave(selected);
    setSaving(false);
  }

  return (
    <div
      ref={backdropRef}
      onClick={(e) => e.target === backdropRef.current && onClose()}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
    >
      <div className="w-full max-w-sm bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <p className="text-sm font-semibold text-foreground">{t("planChange.title")}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Mevcut: <span className="font-medium capitalize">{current}</span></p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Hesap planı ≠ satış (2026-10-15): tek etkinlik satışı sipariştir. */}
        <p data-testid="plan-change-warning" className="mx-4 mt-4 rounded-xl border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
          {t("planChange.accountPlanWarning")}
        </p>

        {/* Plan options */}
        <div className="p-4 space-y-2">
          {plans.map((p) => {
            const active = selected === p.slug;
            const s = getPlanColors(p.slug);
            const isCurrent = p.slug === current;
            // Pro/Max düğün başına satılır → hesap planı olarak atanamaz (sunucu da 400 döner).
            const perWedding = isPerWeddingPlan(p.slug) && !isCurrent;
            return (
              <button
                key={p.slug}
                onClick={() => setSelected(p.slug)}
                disabled={perWedding}
                data-testid={`plan-option-${p.slug}`}
                className={cn(
                  "w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left transition-all cursor-pointer disabled:cursor-not-allowed disabled:opacity-50",
                  active ? s.ring + " bg-card" : "border-transparent bg-muted/40 hover:bg-muted/70"
                )}
              >
                {/* Check circle */}
                <div className={cn(
                  "h-4 w-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-all",
                  active ? cn("border-transparent", s.check) : "border-border"
                )}>
                  {active && <Check className="h-2.5 w-2.5 text-white" />}
                </div>

                <div className="flex-1 min-w-0">
                  <p className={cn("text-sm font-semibold capitalize", active ? s.label : "text-foreground")}>
                    {p.label}
                    {isCurrent && (
                      <span className="ml-2 text-[10px] font-normal text-muted-foreground normal-case tracking-normal">mevcut</span>
                    )}
                  </p>
                  {isPerWeddingPlan(p.slug) && (
                    <p className="text-[11px] text-muted-foreground">{t("planChange.perWeddingOption")}</p>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 pb-4">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted transition-colors cursor-pointer"
          >
            İptal
          </button>
          <button
            onClick={handleSave}
            disabled={!changed || saving}
            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer shadow-sm shadow-primary/20"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Güncelle
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────

export function UserDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const [organizer, setOrganizer] = useState<OrganizerDetail | null>(null);
  const [weddings, setWeddings]   = useState<WeddingRow[]>([]);
  const [plans, setPlans]         = useState<Plan[]>([]);
  /** Paket modeli (058): hesabın paketleri; null → 058 yok, eski hesap planı kuralı. */
  const [packages, setPackages]   = useState<AccountPackagesSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError]         = useState("");
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [planSaved, setPlanSaved]         = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const showToast = useCallback((msg: string, ok = true) => setToast({ msg, ok }), []);
  const clearToast = useCallback(() => setToast(null), []);

  const loadDetail = useCallback(async () => {
    const res = await adminUsersApi.get<{
      organizer: OrganizerDetail; weddings: WeddingRow[]; packagesReady?: boolean; packages?: AccountPackagesSummary | null;
    }>(`/${id}`);
    if (res.ok && res.data) {
      setOrganizer(res.data.organizer);
      setWeddings(res.data.weddings);
      setPackages(res.data.packagesReady && res.data.packages ? res.data.packages : null);
    }
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        const [, plansRes] = await Promise.all([
          loadDetail(),
          adminPlansApi.get<any[]>("/"),
        ]);
        if (plansRes.ok && plansRes.data) {
          setPlans(
            plansRes.data
              .filter((p: any) => p.is_active)
              .map((p: any) => ({ slug: p.slug, label: p.label }))
          );
        }
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : t("common.unknownError"));
      } finally {
        setIsLoading(false);
      }
    })();
  }, [loadDetail, t]);

  const handlePlanSave = async (newPlan: string) => {
    try {
      await adminUsersApi.patch(`/${id}`, { plan: newPlan });
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("common.unknownError");
      showToast(
        msg === "per_wedding_plan" ? t("planChange.perWeddingPlan") : msg === "packages_mode" ? t("adminPackages.packagesMode") : msg,
        false,
      );
      return;
    }
    setOrganizer((prev) => prev ? { ...prev, plan: newPlan } : prev);
    setShowPlanModal(false);
    setPlanSaved(true);
    setTimeout(() => setPlanSaved(false), 3000);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !organizer) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3 text-center">
        <p className="text-sm text-destructive">{error || t("common.error")}</p>
        <AppLink to={paths.users} className="text-sm text-primary hover:underline flex items-center gap-1">
          <ArrowLeft className="h-3.5 w-3.5" /> {t("admin.usersTitle")}
        </AppLink>
      </div>
    );
  }

  const initial  = (organizer.display_name ?? organizer.email)[0].toUpperCase();
  const joinDate = new Date(organizer.created_at).toLocaleDateString(i18n.language, {
    year: "numeric", month: "long", day: "numeric",
  });

  return (
    <>
      {showPlanModal && (
        <PlanModal
          current={organizer.plan}
          plans={plans}
          onClose={() => setShowPlanModal(false)}
          onSave={handlePlanSave}
        />
      )}

      <div className="space-y-6">

        {/* Breadcrumb */}
        <AppLink
          to={paths.users}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {t("admin.usersTitle")}
        </AppLink>

        {/* ── Profile card ──────────────────────────────────────── */}
        <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            <div className="flex justify-center md:justify-start shrink-0">
              <div className="h-24 w-24 md:h-28 md:w-28 rounded-2xl bg-primary/10 border-2 border-primary/20 flex items-center justify-center text-primary text-4xl font-bold select-none">
                {initial}
              </div>
            </div>

            <div className="flex-1 min-w-0 text-center md:text-left space-y-4">
              <div className="space-y-1.5">
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-3">
                  <h1 className="font-serif italic text-3xl text-foreground leading-tight">
                    {organizer.display_name ?? <span className="text-muted-foreground/50 not-italic text-2xl">İsimsiz</span>}
                  </h1>
                  {/* Paket modelinde hesap planı limit belirlemez → rozet yalnız eski kuralda. */}
                  {!packages && <PlanBadge plan={organizer.plan} />}
                </div>
                <p className="text-sm text-muted-foreground">{organizer.email}</p>
              </div>

              <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-5 gap-y-2 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5 text-primary/50" />
                  {organizer.email}
                </span>
                {organizer.phone_e164 && (
                  <span className="flex items-center gap-1.5">
                    <Phone className="h-3.5 w-3.5 text-primary/50" />
                    {organizer.phone_e164}
                  </span>
                )}
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-primary/50" />
                  {joinDate}
                </span>
                <span className="flex items-center gap-1.5">
                  <Heart className="h-3.5 w-3.5 text-primary/50" />
                  {weddings.length} düğün
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Account management ────────────────────────────────── */}
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="px-5 py-3.5 border-b border-border bg-muted/30">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Hesap Yönetimi</p>
          </div>
          {packages ? (
            <UserPackagesCard
              organizerId={organizer.id}
              packages={packages}
              weddings={weddings}
              onChanged={() => void loadDetail().catch(() => showToast(t("common.error"), false))}
              showToast={showToast}
            />
          ) : (
            <>
            <div className="px-5 py-4 flex items-center gap-4">
              <p className="text-sm text-muted-foreground w-28 shrink-0">{t("planChange.rowLabel")}</p>
              <div className="flex items-center gap-3 flex-1">
                <PlanBadge plan={organizer.plan} />
                {planSaved && (
                  <span className="flex items-center gap-1 text-xs text-green-500 font-medium">
                    <Check className="h-3 w-3" /> Güncellendi
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowPlanModal(true)}
                className="shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-border text-xs font-semibold text-foreground hover:border-primary/40 hover:text-primary hover:bg-primary/5 transition-all cursor-pointer"
              >
                {t("planChange.title")}
              </button>
            </div>
            <PlanConversionCard
              key={organizer.plan /* plan değişince (pencere / dönüşüm) önizleme yeniden yüklenir */}
              organizerId={organizer.id}
              onConverted={(plan) => setOrganizer((prev) => (prev ? { ...prev, plan } : prev))}
              showToast={showToast}
            />
            </>
          )}
          <div className="px-5 py-4 border-t border-border/60 flex items-center gap-4">
            <p className="text-sm text-muted-foreground w-28 shrink-0">{t("admin.auditLog")}</p>
            <AppLink
              to={paths.auditForIdentity(organizer.email)}
              className="flex items-center gap-1.5 text-xs font-medium text-primary hover:underline cursor-pointer"
            >
              <ScrollText className="h-3.5 w-3.5" />
              {t("admin.auditLog")} →
            </AppLink>
          </div>
        </div>

        {/* ── Weddings ──────────────────────────────────────────── */}
        <div className="space-y-3">
          <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
            {t("wedding.title")} <span className="font-normal normal-case tracking-normal">({weddings.length})</span>
          </h2>

          {weddings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-center rounded-2xl border border-border">
              <Heart className="h-8 w-8 text-primary/30" />
              <p className="text-sm text-muted-foreground">{t("wedding.noWeddingsAdmin")}</p>
            </div>
          ) : (
            <div className="rounded-2xl border border-border overflow-hidden">
              <table className="w-full text-left border-collapse">
                <thead className="border-b border-border bg-muted/30">
                  <tr>
                    <th className="px-5 py-3.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Düğün</th>
                    <th className="px-5 py-3.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground hidden sm:table-cell">Tarih</th>
                    <th className="px-5 py-3.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground hidden md:table-cell">Konum</th>
                    <th className="px-5 py-3.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t("admin.packageColumn")}</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {weddings.map((w) => (
                    <tr
                      key={w.id}
                      className={cn(
                        "transition-colors group",
                        w.status === "archived" ? "opacity-50 hover:opacity-70" : "hover:bg-primary/5"
                      )}
                    >
                      <td className="px-5 py-4">
                        <AppLink to={paths.wedding.dashboard(w.slug)} className="block">
                          <p className={cn(
                            "text-sm font-semibold group-hover:text-primary transition-colors",
                            w.status === "archived" && "line-through text-muted-foreground"
                          )}>
                            {w.title}
                          </p>
                          <span className="mt-0.5 inline-block font-mono text-[11px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                            /{w.slug}
                          </span>
                        </AppLink>
                      </td>
                      <td className="px-5 py-4 text-sm text-muted-foreground hidden sm:table-cell whitespace-nowrap">
                        {w.date_time
                          ? new Date(w.date_time).toLocaleDateString(i18n.language)
                          : <span className="text-muted-foreground/40">—</span>}
                      </td>
                      <td className="px-5 py-4 text-sm text-muted-foreground hidden md:table-cell max-w-[160px] truncate">
                        {w.location ?? <span className="text-muted-foreground/40">—</span>}
                      </td>
                      <td
                        data-testid={`user-wedding-package-${w.slug}`}
                        className={cn("px-5 py-4 text-xs whitespace-nowrap", w.package?.source === "suspended" ? "font-semibold text-red-600 dark:text-red-400" : "text-muted-foreground")}
                      >
                        {weddingPackageText(t, w.package)}
                      </td>
                      <td className="px-5 py-4">
                        <AppLink
                          to={paths.wedding.dashboard(w.slug)}
                          className="flex items-center justify-end text-muted-foreground group-hover:text-primary transition-colors"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </AppLink>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {toast && <Toast msg={toast.msg} ok={toast.ok} onDone={clearToast} />}
    </>
  );
}
